import { Injectable } from '@angular/core';
import {
  AttributeBase,
  AttributeType,
  CurrencyPair,
  CutOffTimesAndFixingSources,
  GenericLeg,
  KeyValue,
  LegAttribute,
  PricingResultsInfo,
  ProductGroupings,
  ProductInfo,
  ProductSpecification,
  RfqProductGrouping,
  ScheduleColumn,
  StrategyDetailResponse,
  UserSettings
} from '@approot/models';
import { DateUtilities, Utilities, clone } from '@approot/utilities';
import dayjs from 'dayjs';
import { union } from 'lodash';
import { Memoize } from 'lodash-decorators';
import { Observable } from 'rxjs';
import { AlertsService } from '../alerts/alerts.service';
import { AuthService } from '../auth/auth.service';
import { ConfigService } from '../config/config.service';
import { marginStyles, singleLegTitleMapper } from '../rfq/rfq-tab/rfq-tab';
import { ExecutionContext } from '../rfq/rfq.types';
import { TradesCaptureService } from './../trade-capture/trades-capture.service';

interface AddExtraRfqAttributesOptions {
  productSpec: ProductSpecification;
  isVolBased: boolean;
  addDf: boolean;
  isProviderQuote: boolean;
  userSettings: UserSettings;
  removeSpot: boolean;
  addWhiteLabelAttributes: boolean;
}
@Injectable({
  providedIn: 'root'
})
export class ProductsConfigService {
  public productInfo: ProductInfo;
  public pricingResultsInfo: PricingResultsInfo;
  public cutOffTimesAndFixingSources: CutOffTimesAndFixingSources;
  public scheduleColumnNames: ScheduleColumn[];
  public rfqProductsGroupings: RfqProductGrouping[];
  public productGroupings: ProductGroupings[];
  private pricingResultsSortingArray = [
    'premiumQuoteCurrencyInPercent',
    'premiumQuoteCurrencyPips',
    'premiumBaseCurrency',
    'premiumBaseCurrencyInPercent',
    'upfrontPremiumBsMid',
    'spotDelta',
    'spotDeltaPercent',
    'forwardDelta',
    'forwardDeltaPercent',
    'brokerDelta',
    'brokerDeltaPercent',
    'gamma',
    'vega',
    'riskies',
    'flies'
  ];
  private marketDataSortingArray = [
    'forward',
    'forwardPoints',
    'volAtm',
    'vol'
  ];
  private markUpSortingArray = [
    'receivedInterest',
    'paidInterest',
    'dayCount',
    'DV01',
    'fairRate',
    'fairRateForStrike',
    'fairStrikeForRate',
    'firstSpotDelta',
    'secondSpotDelta',
    'firstVega',
    'secondVega',
    'numeraire',
    'correlation',
    'parStrike',
    'events'
  ];
  private dcdSortingArray = [
    'marginInAlternativeCurrency',
    'optionNotional',
    'exerciseProbability',
    'interestConvention'
  ];
  constructor(
    private tradesCaptureService: TradesCaptureService,
    private configService: ConfigService,
    private alertsService: AlertsService,
    private authService: AuthService
  ) {}

  public setProductsConfig(response: StrategyDetailResponse) {
    const attributesDisplayList = response.userSettings.order.find(
      b => b.identifier === 'product-attributes'
    ).data;
    this.productInfo = {
      attributesDisplayList: attributesDisplayList,
      directRfqAttributesDisplayList:
        response.userSettings.directRfqProductsOrder?.data,
      currencyPairs:
        response.userSettings.currencyPairs?.map(a => a.currencyPair) || [],
      productSpecifications: response.userSettings.products || [],
      directRfqProducts: response.userSettings.directRfqProducts || [],
      tradeAttributes: this.getTradeAttributes(
        response.userSettings.tradeAttributes?.attributes,
        attributesDisplayList
      )
    };
    this.sortCurrencyPairs(response.userSettings.currencyPairs);
    this.setPricingResultsInfo(response.userSettings);
    this.setCutOffTimesAndFixingSources(response.userSettings);
  }

  public handleRfqProducts(
    executionContext: ExecutionContext,
    shouldRfq: boolean
  ): void {
    if (!executionContext || !shouldRfq) {
      return;
    }

    const clonedProductSpecifications = clone(
      this.productInfo.productSpecifications
    );

    const productSpecifications = this.configService.userSettings().usePerseus
      ? clonedProductSpecifications
      : clonedProductSpecifications.filter(prodSpec =>
          singleLegTitleMapper(prodSpec.productName, false)
        );

    this.rfqProductsGroupings = clone(this.productGroupings)
      .map(productGroup => {
        return {
          ...productGroup,
          items: productGroup.items
            .filter(product =>
              productSpecifications.find(
                x => x.productType.shortName === product
              )
            )
            .map(prodSpec => {
              const product = productSpecifications.find(
                y => y.productType.shortName === prodSpec
              );
              return {
                name: product.productType.longName,
                shortName: product.productType.shortName,
                currencyPair: product.attributes?.find(
                  l => l.shortName === 'cross'
                )?.value
              };
            })
        };
      })
      .filter(productGroup => productGroup.items.length);
  }

  public addExtraRfqAttributesToList(
    isPremiumBased: boolean,
    isVolBased: boolean,
    addDF: boolean,
    userSettings: UserSettings,
    addWhiteLabelAttributes: boolean
  ): LegAttribute[] {
    const attributesDisplayList = clone(this.productInfo.attributesDisplayList);

    if (isPremiumBased || isVolBased) {
      if (addDF) {
        attributesDisplayList.push(
          {
            shortName: 'discountFactorCurrency',
            longName: 'DF Currency'
          },
          {
            shortName: 'discountFactor',
            longName: 'DF'
          }
        );
      }

      if (isVolBased) {
        attributesDisplayList.push(
          {
            shortName: 'rfqPremiumMargin',
            longName: 'Premium Margin'
          },
          {
            shortName: 'rfqVolMargin',
            longName: 'Vol Margin'
          },
          {
            shortName: 'premiumValueType',
            longName: 'Premium Value'
          },
          {
            shortName: 'premiumSettlementDate',
            longName: 'Premium Date'
          }
        );
      }
    }

    if (userSettings.useWhiteLabelMargin && addWhiteLabelAttributes) {
      const fields = [
        {
          shortName: 'marginStyle',
          longName: 'Margin Style'
        },
        {
          shortName: 'margin',
          longName: 'Margin'
        }
      ];

      if (userSettings.useRetrocessionFee) {
        fields.push({
          shortName: 'retrocessionFee',
          longName: 'Retrocession Fee'
        });
      }
      attributesDisplayList.push(...fields);
    }
    return attributesDisplayList;
  }

  public addExtraRfqAttributes(
    options: AddExtraRfqAttributesOptions
  ): ProductSpecification {
    const productSpecCopy = clone(options.productSpec);
    let productAttributes = productSpecCopy.attributes;

    if (options.removeSpot) {
      productAttributes = productAttributes.filter(x => x.shortName !== 'spot');
    }

    if (options.addDf) {
      productAttributes.push(
        {
          readOnly: 'true',
          shortName: 'discountFactorCurrency',
          type: 'string',
          optional: true
        },
        {
          shortName: 'discountFactor',
          type: 'string',
          validators: ['percentage'],
          optional: true,
          format: 'percentage'
        }
      );
    }

    if (options.isVolBased && !options.isProviderQuote) {
      productAttributes.push(
        {
          shortName: 'rfqPremiumMargin',
          type: 'string',
          optional: true,
          validators: ['numericShorthand']
        },
        {
          shortName: 'rfqVolMargin',
          optional: true,
          type: 'string',
          validators: ['percentage'],
          format: 'percentage'
        },
        {
          shortName: 'premiumValueType',
          optional: false,
          type: 'enum',
          value: ['Spot', 'Forward']
        },
        {
          shortName: 'premiumSettlementDate',
          optional: false,
          type: 'editableDate',
          validators: ['dateShorthand']
        }
      );
    }

    if (options.addWhiteLabelAttributes) {
      this.addWhiteLabelMarginAttributes(
        options.userSettings,
        productAttributes
      );
    }

    productSpecCopy.attributes = productAttributes;
    return productSpecCopy;
  }

  public addExtraAttributeValidators(
    productSpec: ProductSpecification
  ): ProductSpecification {
    const productSpecClone = clone(productSpec);
    const volInputAttributes = productSpecClone.attributes.find(
      x => x.shortName === 'volInput'
    );
    if (volInputAttributes) {
      volInputAttributes.attributes?.forEach(attr => {
        attr.optional = false;
        if (!attr.validators) {
          attr.validators = [];
        }
        if (!attr.validators.includes('positiveNumber')) {
          attr.validators.push('positiveNumber');
        }
      });
    }
    return productSpecClone;
  }

  private sortCurrencyPairs(currencyPairs: CurrencyPair[]): void {
    this.productInfo.productSpecifications.forEach(
      product =>
        product.attributes
          .find(attribute => attribute.shortName === 'cross')
          ?.value?.sort(
            (a, b) =>
              currencyPairs.findIndex(pair => pair.currencyPair === a) -
              currencyPairs.findIndex(pair => pair.currencyPair === b)
          )
    );
  }

  private setCutOffTimesAndFixingSources(userSettings: UserSettings) {
    this.cutOffTimesAndFixingSources = {
      currencyPairInfo:
        userSettings.currencyPairs?.map(a => {
          return {
            currencyPair: a.currencyPair,
            cutOffs: a.cutOffs,
            fixingSources: a.fixingSources,
            forwardPointsMultiplier: a.forwardPointsMultiplier,
            displayDecimals: +a.displayDecimals,
            defaultPremiumCurrency: a.defaultPremiumCurrency
          };
        }) || [],
      cutOffs: userSettings.cutofftime,
      fixingSources: userSettings.fixingsource
    };
  }

  @Memoize()
  public decimalPlacesForCross(cross: string): number {
    const cpi = this.cutOffTimesAndFixingSources?.currencyPairInfo?.find(
      cp => cp.currencyPair === cross
    );
    // Sometimes this function is called with a dud cross, e.g., a currency
    return cpi?.displayDecimals ?? 5;
  }

  public crossDecimalFormatter(value: any, cross: string) {
    if (cross && value !== '' && !isNaN(value)) {
      value = +value; // allow 1.30000 to turn into 1.3
      // if there is a cross and the value is not a empty string
      const dp = this.decimalPlacesForCross(cross);
      return Utilities.roundNumberToDp(value, dp);
    }
    return value;
  }

  @Memoize()
  public decimalPlacesForCrossPipe(cross: string): string {
    const dp = this.decimalPlacesForCross(cross);
    return `1.${dp}-${dp}`;
  }

  // structures the product into a format that the backend likes
  public productFormatter(
    product: GenericLeg,
    attributes: LegAttribute[]
  ): KeyValue {
    let trade: KeyValue = {};
    const allAttributes = this.getAllAttributes(attributes) as LegAttribute[];
    Object.keys(product).forEach(key => {
      const attribute = allAttributes.find(
        attribute => attribute.shortName === key
      );
      if (
        attribute?.validators?.includes('formattedDecimal') ||
        attribute?.currencyAttribute
      ) {
        trade[key] =
          product[key] === Utilities.customText
            ? product[key]
            : Utilities.formattedNumberToNumber(product[key]);
        return;
      }

      if (key === 'delta' || key === 'premiumDate' || key === 'premiumType') {
        return;
      }
      if (
        !product[key] &&
        (key === 'rfqPremiumMargin' || key === 'rfqVolMargin')
      ) {
        // exclude all falsy values for these keys (backend doesn't '' for example)
        return;
      }
      if (product[key] == null || product[key] === '') {
        // ng-select default empty value has to be null, as this is how it recognizes the component as being empty
        // allowing for things such as placeholders to be displayed. The backend doesn't accept nulls.
        trade[key] = '';
        return;
      }

      if (Utilities.attributeBelongsToWhichCross(key)) {
        // format the data that needs to be formatted based on its cross before its sent of.
        product[key] = this.crossDecimalFormatter(
          product[key],
          product[Utilities.attributeBelongsToWhichCross(key)]
        );
      }

      if (allAttributes) {
        const foundAttribute = allAttributes.find(x => x.shortName === key);
        if (foundAttribute?.format === 'percentage') {
          trade[key] =
            product[key] === Utilities.customText
              ? product[key]
              : Utilities.percentageToNumber(product[key]);
          return;
        }
      }
      switch (key) {
        case 'fixingNumber':
        case 'delta':
          break;
        case 'windowDate':
        case 'windowStartDate':
        case 'windowEndDate':
        case 'expiryDate':
        case 'settlementDate':
        case 'firstFixingDate':
        case 'firstExpirationDate':
        case 'firstSettlementDate':
        case 'lastFixingDate':
        case 'lastExpirationDate':
        case 'lastSettlementDate':
        case 'redemptionDate':
        case 'depositDate':
        case 'strikeFixingDate':
        case 'tradeDate':
        case 'farDate':
        case 'nearDate':
        case 'premiumPaymentDate':
        case 'valuationAdjustmentAmortizationEndDate':
        case 'mtcDate':
        case 'valuationAdjustmentAmortizationStartDate':
        case 'startDate':
        case 'endDate':
        case 'swaptionExpiryDate':
        case 'swaptionSettlementDate':
        case 'swapStartDate':
        case 'swapEndDate':
        case 'payDate':
        case 'setDate':
        case 'fixingDate':
        case 'exerciseDate':
        case 'notificationDate':
        case 'firstExerciseDate':
        case 'premiumDate':
        case 'premiumSettlementDate':
        case 'xccySwapEndDate':
        case 'xccySwapStartDate':
          trade[key] = DateUtilities.isPrettyDate(product[key])
            ? DateUtilities.getDateInISOFormat(product[key])
            : product[key];
          break;
        case 'spotDate':
          trade[key] = dayjs(product[key], 'D MMM YY', false).format(
            'YYYY-MM-DD'
          );
          break;
        case 'productType':
          trade[key] = product[key].toLowerCase().replace(/\s/g, '');
          break;
        case 'tradeId':
          trade[key] = product[key].toString();
          break;
        case 'notional':
        case 'counterNotional':
        case 'notionalShort':
        case 'notionalLong':
        case 'callNotional':
        case 'putNotional':
        case 'secondNotional':
        case 'capLossAmount':
        case 'putAmount':
        case 'instalmentAmount':
        case 'valuationAdjustment':
        case 'midPv':
        case 'premium':
        case 'notional1':
        case 'notional2':
        case 'counterNotional1':
        case 'counterNotional2':
        case 'fixedAmountLowerEkiZone':
        case 'fixedAmountUpperEkiZone':
        case 'targetProfit':
        case 'nearAmount':
        case 'farAmount':
        case 'retrocessionFeeInPremiumCurrency':
        case 'marginAmountInPremiumCurrency':
        case 'tradingMargin':
        case 'totalNotional':
        case 'strike':
        case 'strike1':
        case 'strike2':
        case 'spot':
        case 'spotAsk':
        case 'spotBid':
          trade[key] =
            product[key] === Utilities.customText
              ? product[key]
              : parseFloat(('' + product[key]).replace(/,/g, ''));
          break;
        case 'depositRate':
        case 'targetMarkUp':
        case 'volStrike':
        case 'volBarrier':
        case 'fixedLegRate':
        case 'ratesStrike':
        case 'ratesBarrier':
        case 'dualDigitalRatesStrike':
        case 'treasuryRate':
        case 'volAsk':
        case 'volBid':
        case 'vol1Bid':
        case 'vol2Bid':
        case 'vol1Ask':
        case 'vol2Ask':
        case 'discountFactor':
        case 'cmsCorrel':
        case 'fixedLegRate1':
        case 'fixedLegRate2':
          // remove in the future
          trade[key] =
            product[key] === Utilities.customText
              ? product[key]
              : Utilities.percentageToNumber(product[key]);
          break;
        case 'cutOff':
          trade[key] = this.cutOffTimesAndFixingSources.cutOffs.find(
            x => x.longName === product[key]
          ).code;
          break;
        case 'fixingSource':
        case 'forcedCashSettlementFixingSource':
          trade[key] = this.cutOffTimesAndFixingSources.fixingSources.find(
            x => x.longName === product[key]
          ).code;
          break;
        case 'margin':
        case 'marginBid':
        case 'marginAsk':
          let isPercent = false;
          if (product.marginStyle && product.marginType) {
            const errorMessage =
              'Both Margin Style and Margin Type are present';
            this.alertsService.addAlert({ message: errorMessage });
            throw new Error(errorMessage);
          }

          if (product.marginStyle) {
            isPercent = isMarginStylePercentage(product.marginStyle);
          } else if (product.marginType) {
            isPercent = product.marginType === '% p.a.';
          }

          if (isPercent) {
            trade[key] = Utilities.percentageToNumber(product[key]);
          } else {
            trade[key] =
              product[key] === Utilities.customText
                ? product[key]
                : parseFloat(product[key].replace(/,/g, ''));
          }
          break;
        case 'retrocessionFee':
          if (isMarginStylePercentage(product.marginStyle)) {
            trade[key] = Utilities.percentageToNumber(product[key]);
          } else {
            trade[key] =
              product[key] === Utilities.customText
                ? product[key]
                : parseFloat(product[key].replace(/,/g, ''));
          }
          break;
        case 'schedule':
        case 'floatingLegSchedule':
        case 'fixedLegSchedule':
        case 'fixedLegSchedule1':
        case 'fixedLegSchedule2':
        case 'exerciseSchedule':
          trade[key] = product[key].map((row: any) =>
            this.productFormatter(row, attributes)
          );
          break;
        case 'isExerciseDate':
          if (product[key] === true) {
            // booleans should be in a string format
            trade[key] = 'true';
          } else if (product[key] === false) {
            trade[key] = 'false';
          } else {
            trade[key] = product[key];
          }
          break;
        case 'rfqPremiumMargin':
          trade[key] = Utilities.formattedNumberToNumber(product[key]);
          if (isNaN(+trade[key])) {
            console.warn('rfqPremiumMargin is not a number ' + product[key]);
            trade.invalid = 1;
          }
          break;
        case 'rfqVolMargin':
          trade[key] = Utilities.percentageToNumber(product[key]);
          if (isNaN(+trade[key])) {
            console.warn('rfqVolMargin is not a number ' + product[key]);
            trade.invalid = 1;
          }
          break;
        default:
          trade[key] = product[key];
          break;
      }
    });
    if (trade.invalid) {
      return null;
    }
    return trade;
  }

  private addWhiteLabelMarginAttributes(
    userSettings: UserSettings,
    productAttributes: LegAttribute[]
  ): void {
    if (userSettings.useWhiteLabelMargin) {
      const margin = userSettings.parent.enableTwoWayMargin
        ? {
            attributes: [
              {
                shortName: 'marginBid',
                type: 'string' as AttributeType
              },
              {
                shortName: 'marginAsk',
                type: 'string' as AttributeType
              }
            ],
            shortName: 'margin',
            type: 'bidAskList' as AttributeType
          }
        : {
            shortName: 'margin',
            type: 'string' as AttributeType
          };

      [
        {
          shortName: 'marginStyle',
          optional: true,
          type: 'enum' as AttributeType,
          value: marginStyles
        },
        margin
      ].forEach(control => {
        if (
          !productAttributes.find(
            attribute => attribute.shortName === control.shortName
          )
        ) {
          productAttributes.push(control);
        }
      });

      if (
        userSettings.useRetrocessionFee &&
        !productAttributes.find(
          attribute => attribute.shortName === 'retrocessionFee'
        )
      ) {
        productAttributes.push({
          shortName: 'retrocessionFee',
          type: 'string'
        });
      }
    }
  }

  private setPricingResultsInfo(userSettings: UserSettings) {
    this.pricingResultsInfo = {
      resultSpecifications:
        userSettings.results.pricing?.attributes.sort((a, b) => {
          return (
            this.pricingResultsSortingArray.indexOf(a.shortName) -
            this.pricingResultsSortingArray.indexOf(b.shortName)
          );
        }) || [],
      marketDataSpecifications:
        userSettings.results.market?.attributes.sort((a, b) => {
          return (
            this.marketDataSortingArray.indexOf(a.shortName) -
            this.marketDataSortingArray.indexOf(b.shortName)
          );
        }) || [],
      markUpSpecifications:
        userSettings.results.extraresults?.attributes.sort((a, b) => {
          return (
            this.markUpSortingArray.indexOf(a.shortName) -
            this.markUpSortingArray.indexOf(b.shortName)
          );
        }) || [],
      dcdSpecifications:
        userSettings.results.dcdresults?.attributes.sort((a, b) => {
          return (
            this.dcdSortingArray.indexOf(a.shortName) -
            this.dcdSortingArray.indexOf(b.shortName)
          );
        }) || []
    };
  }

  private getTradeAttributes(
    tradeAttributes: LegAttribute[],
    attributesDisplayList: LegAttribute[]
  ): LegAttribute[] {
    if (tradeAttributes) {
      tradeAttributes.forEach(attribute => {
        // loop through all the trade attributes labels and mark them as a trade attribute
        const isAttributeTrade = attributesDisplayList.find(
          attributeItem => attribute.shortName === attributeItem.shortName
        );

        if (isAttributeTrade) {
          isAttributeTrade.group = 'tradeAttributes';
        }
        attribute.disabled = 'true'; // set a disable on all trade attributes on creation
      });
      this.tradesCaptureService.displayTradeViewButton = true;
      return tradeAttributes; // stored the trade attributes in the legservice
    }
    return [];
  }

  public getAllAttributes(
    attributes: LegAttribute[] | AttributeBase<any>[]
  ): LegAttribute[] | AttributeBase<any>[] {
    if (!attributes) {
      return [];
    }

    return attributes.flatMap(attribute => {
      if (!attribute.attributes) {
        return attribute;
      } else {
        return this.getAllAttributes(attribute.attributes);
      }
    });
  }

  public getCrossFromCurrencies(currency1: string, currency2: string): string {
    const ccy1 = currency1 + currency2;
    const ccy2 = currency2 + currency1;
    const pairsFound = this.cutOffTimesAndFixingSources.currencyPairInfo
      .filter(
        pairInfo =>
          pairInfo.currencyPair === ccy1 || pairInfo.currencyPair === ccy2
      )
      .map(info => info.currencyPair);

    if (pairsFound.includes(ccy1)) {
      return ccy1;
    } else if (pairsFound.includes(ccy2)) {
      return ccy2;
    }
    return '';
  }

  public getFixingSourceCode(longName: string): string {
    return this.cutOffTimesAndFixingSources.fixingSources.find(
      x => x.longName === longName
    )?.code;
  }

  public getCutOffCode(longName: string): string {
    return this.cutOffTimesAndFixingSources.cutOffs.find(
      x => x.longName === longName
    )?.code;
  }

  public getDCICutOffs(
    depositCurrency: string,
    alternativeCurrency: string
  ): Observable<string[]> {
    const task = 'getCutOffs';
    const product = {
      productType: 'dual-currencydeposit',
      depositCurrency,
      alternativeCurrency
    };
    return this.authService.fxPost<string[]>(
      { task, product },
      'get-user-info',
      `task=${task}`
    );
  }

  public getFixingSourcesOrCutOffs(
    currencyPairs: string[],
    field: 'fixingSources' | 'cutOffs'
  ): string[] {
    try {
      return union(
        ...this.cutOffTimesAndFixingSources.currencyPairInfo
          .filter(x => currencyPairs.includes(x.currencyPair))
          .map(x => x[field])
      ).map(
        code =>
          this.cutOffTimesAndFixingSources[field].find(y => y.code === code)
            .longName
      );
    } catch (error) {
      return [];
    }
  }
}

export function canDisplayDF(productType: string): boolean {
  return [
    'vanilla',
    'vanillaoption',
    'ndo',
    'straddle',
    'strangle',
    'riskreversal'
  ].includes(productType);
}

function isMarginStylePercentage(marginStyle: string): boolean {
  return marginStyle === 'Percent Base' || marginStyle === 'Percent Term';
}
